import Fade from '@mui/material/Fade';
import type { Theme } from '@mui/material/styles';
import type { TooltipProps } from '@mui/material/Tooltip';
import { makeStyles } from 'tss-react/mui';

export const navBarWidth = 56;
export const navBarWidthCollapsed = 52;
export const navBarWidthOpen = 230;
export const navBarDiff = navBarWidth - navBarWidthOpen;
export const dividerSpacing = '8px';
export const navItemInset = 8;
export const navTooltipProps = {
  placement: 'right',
  enterDelay: 200,
  slots: { transition: Fade },
} satisfies Partial<TooltipProps>;

const navHoverTransitionDuration = 120;

export const navToggleTransition = (theme: Theme): { easing: string; duration: number } => ({
  easing: theme.transitions.easing.easeInOut,
  duration: 200,
});

export const navLabelFadeIn = (theme: Theme): string =>
  theme.transitions.create('opacity', { duration: 150, delay: 50 });

export const navLabelFadeOut = (theme: Theme): string => theme.transitions.create('opacity', { duration: 120 });

export const useNavBarStyle = makeStyles<void, 'groupDivider' | 'sectionTitle' | 'sectionDivider'>()(
  (theme, _, classes) => ({
    navbarClosed: {
      zIndex: theme.zIndex.drawer,
      backgroundColor: theme.palette.background.paper,
      width: navBarWidth,
      left: 0,
      height: '100%',
      overflow: 'hidden',
      transition: 'width .25s ease',
      transitionDelay: '300ms',
      boxShadow: theme.shadows[24],
    },
    navbar: {
      zIndex: theme.zIndex.drawer,
      backgroundColor: theme.palette.background.paper,
      width: navBarWidthOpen,
      left: 0,
      height: '100%',
      overflow: 'hidden',
      transition: 'width .25s ease',
      transitionDelay: '300ms',
      boxShadow: theme.shadows[24],
      [`.${classes.groupDivider}`]: {
        display: 'flex',
      },
      [`.${classes.sectionTitle}`]: {
        opacity: 1,
        transition: navLabelFadeIn(theme),
      },
      [`.${classes.sectionDivider}`]: {
        opacity: 0,
        transition: 'none',
      },
    },
    navbarToggle: {
      transition: `${theme.transitions.create(['transform', 'width'], navToggleTransition(theme))} !important`,
      boxShadow: `1px 0 0 ${theme.palette.divider}`,
    },
    navbarToggleClosed: {
      width: navBarWidthCollapsed,
    },
    group: {
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
    },
    groupDivider: {
      display: 'flex',
      backgroundColor: theme.palette.border.light,
      height: '1px',
      marginTop: dividerSpacing,
      marginBottom: dividerSpacing,
      width: '100%',
    },
    sectionHeader: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      boxSizing: 'border-box',
      height: theme.spacing(3.5),
      paddingTop: theme.spacing(1),
    },
    sectionHeaderExpandable: {
      cursor: 'pointer',
      [`&:hover .${classes.sectionDivider}`]: {
        backgroundColor: theme.palette.primary.main,
      },
    },
    sectionTitle: {
      paddingLeft: theme.spacing(2),
      color: theme.palette.text.secondary,
      opacity: 0,
      transition: navLabelFadeOut(theme),
    },
    sectionDivider: {
      position: 'absolute',
      top: '50%',
      left: theme.spacing(2),
      right: theme.spacing(2),
      height: 1,
      borderRadius: theme.shape.radius.full,
      backgroundColor: theme.palette.divider,
      opacity: 1,
      transition: [
        theme.transitions.create('background-color', { duration: navHoverTransitionDuration }),
        theme.transitions.create('opacity', { duration: 180, delay: navToggleTransition(theme).duration }),
      ].join(', '),
    },
    untitledSection: {
      paddingTop: theme.spacing(1),
    },
    groupDividerStatic: {
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
      display: 'flex',
      backgroundColor: theme.palette.border.light,
      height: '1px',
      marginTop: dividerSpacing,
      marginBottom: dividerSpacing,
      width: 'inherit',
    },
  }),
);

const useStylesInternal = makeStyles<
  void,
  | 'navMenuItem'
  | 'navText'
  | 'selectedNavText'
  | 'refreshNavItem'
  | 'navToggleButton'
  | 'refreshNavWrapper'
  | 'navFlyoutChevron'
>()((theme, _, classes) => {
  const itemVerticalSpacing = 6;
  const iconMargin = 6;
  return {
    navMenuItem: {
      color: theme.palette.icon.main,
      opacity: 1,
      justifyContent: 'unset',
      whiteSpace: 'nowrap',
      display: 'flex',
      flexDirection: 'row',
      textTransform: 'none',
      alignItems: 'center',
      textAlign: 'center',
      outline: 'none',
      padding: `${itemVerticalSpacing}px 0px ${itemVerticalSpacing}px 12px`,
      width: navBarWidthOpen,
      '&:hover': {
        opacity: 1,
        color: `${theme.palette.primary.main} !important`,
        background: `${theme.palette.action.hover} !important`,
        cursor: 'pointer',
      },
      '&:focus': {
        opacity: 1,
        color: theme.palette.primary.main,
        background: theme.palette.action.hover,
      },
      transition: 'all 200ms cubic-bezier(0.550, 0.085, 0.680, 0.530)',
    },
    sectionedNavItems: {
      [`& .${classes.navMenuItem}`]: {
        boxSizing: 'border-box',
        width: `calc(100% - ${navItemInset * 2}px)`,
        height: theme.spacing(3.75),
        margin: `0 ${navItemInset}px`,
        padding: theme.spacing(0, 0, 0, 0.25),
        borderRadius: theme.shape.radius.md,
        overflow: 'hidden',
        transition: theme.transitions.create(['background-color', 'color'], {
          duration: navHoverTransitionDuration,
        }),
        color: theme.palette.icon.dark,
        '&:focus': {
          color: theme.palette.primary.main,
        },
      },
      [`& .${classes.navMenuItem} + .${classes.navMenuItem}`]: {
        marginTop: theme.spacing(0.25),
      },
      [`& .${classes.navText}, & .${classes.selectedNavText}`]: {
        marginLeft: theme.spacing(0.75),
      },
      [`& .${classes.navMenuItem}:hover .${classes.navText}, & .${classes.navMenuItem}:focus-visible .${classes.navText}`]:
        {
          color: theme.palette.primary.main,
        },
      [`& .${classes.navMenuItem}:hover .${classes.navFlyoutChevron}, & .${classes.navMenuItem}:focus-visible .${classes.navFlyoutChevron}`]:
        {
          color: theme.palette.primary.main,
        },
      [`& .${classes.selectedNavText}`]: {
        color: theme.palette.text.primary,
        fontWeight: theme.typography.paragraphMedium.fontWeight,
      },
      [`& .${classes.refreshNavItem}`]: {
        width: 'auto',
      },
      [`& .${classes.navToggleButton}`]: {
        marginTop: theme.spacing(0.25),
        width: navBarWidthCollapsed - navItemInset * 2,
      },
      [`& .${classes.navToggleButton}:focus:not(:focus-visible)`]: {
        color: theme.palette.icon.dark,
        background: 'transparent',
      },
    },
    sectionedNavItemsExpanded: {
      [`& .${classes.navMenuItem}:not(.${classes.navToggleButton}):not(:focus)`]: {
        color: theme.palette.icon.light,
      },
      [`& .${classes.navToggleButton}, & .${classes.navToggleButton}:focus:not(:focus-visible)`]: {
        color: theme.palette.text.primary,
      },
    },
    refreshNavItem: {},
    navToggleButton: {},
    refreshNavWrapper: {},
    navBottomRow: {
      '&&': {
        flexDirection: 'row',
        alignItems: 'center',
      },
      [`& .${classes.refreshNavWrapper}`]: {
        flex: '1 1 0',
        minWidth: 0,
      },
      [`& .${classes.refreshNavItem}`]: {
        marginRight: theme.spacing(0.25),
      },
      [`& .${classes.navToggleButton}`]: {
        marginTop: 0,
        marginLeft: 0,
        flex: 'none',
      },
    },
    selectedItem: {
      color: `${theme.palette.primary.main} !important`,
      background: `${theme.palette.action.hover} !important`,
      '&:hover': {
        opacity: 1,
        color: `${theme.palette.primary.main} !important`,
        background: `${theme.palette.action.hover} !important`,
        cursor: 'pointer',
      },
    },
    selectedNavText: {
      color: theme.palette.primary.main,
      marginLeft: 16,
      textAlign: 'left',
    },
    navText: {
      color: theme.palette.text.primary,
      marginLeft: 16,
      textAlign: 'left',
    },
    navBarIconSpan: {
      display: 'flex',
      height: 20,
      alignItems: 'center',
      justifyContent: 'center',
    },
    navIcon: {
      width: 20,
      height: 20,
      fill: '',
      marginLeft: iconMargin,
      marginRight: iconMargin,
    },
    navFlyoutChevron: {
      flex: 'none',
      marginRight: theme.spacing(1),
      color: theme.palette.icon.light,
    },
    navToggleIcon: {
      flexShrink: 0,
      transition: theme.transitions.create('transform', {
        ...navToggleTransition(theme),
        delay: navToggleTransition(theme).duration,
      }),
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
      },
    },
    navToggleIconCollapsed: {
      transform: 'scaleX(-1)',
    },
  };
});

export const useNavItemStyles = () => {
  return useStylesInternal();
};
